import { create } from 'zustand';
import { Team } from '@/types/cricket';
import { 
  fetchTeams, 
  createTeam, 
  updateTeam as updateTeamAPI,
  deleteTeam as deleteTeamAPI
} from '@/services/supabaseService';
import { toast } from '@/components/ui/use-toast';

interface TeamStore {
  teams: Team[];
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchTeams: () => Promise<void>;
  addTeam: (team: Omit<Team, 'id' | 'players'> & { logoFile?: File }) => Promise<void>;
  updateTeam: (team: Team) => Promise<void>;
  deleteTeam: (teamId: string) => Promise<void>;
}

const useTeamStore = create<TeamStore>((set, get) => ({
  teams: [],
  loading: false,
  error: null,
  
  fetchTeams: async () => {
    set({ loading: true, error: null });
    try {
      const teamsData = await fetchTeams();
      set({ teams: teamsData, loading: false });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error fetching teams:", error);
      set({ 
        error: message || "Failed to load teams", 
        loading: false 
      });
      toast({
        title: "Error",
        description: "Failed to load teams",
        variant: "destructive",
      });
    }
  },
  
  addTeam: async (team) => {
    try {
      const { name, shortCode, color, tournamentId, logoFile } = team;
      const newTeam = await createTeam({ name, shortCode, color, tournamentId, logoFile });
      set((state) => ({ 
        teams: [newTeam, ...state.teams] 
      }));
      toast({
        title: "Success",
        description: "Team created successfully!",
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error creating team:", error);
      toast({
        title: "Error",
        description: message || "Failed to create team",
        variant: "destructive",
      });
      throw error;
    }
  },

  updateTeam: async (team) => {
    try {
      // Ensure all required fields for UpdateTeamPayload
      const { id, name, shortCode, color, tournamentId, players } = team;
      const logoFile = (team as unknown as { logoFile?: File }).logoFile ?? undefined;
      const logoUrl = (team as unknown as { logoUrl?: string }).logoUrl ?? undefined;
      const updatedTeam = await updateTeamAPI({ id, name, shortCode, color, tournamentId, logoFile, logoUrl, players });
      set((state) => ({
        teams: state.teams.map((t) =>
          t.id === team.id ? updatedTeam : t
        ),
      }));
      toast({
        title: 'Success',
        description: `${updatedTeam.name} updated successfully!`,
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('Error updating team:', error);
      toast({
        title: 'Error',
        description: message || 'Failed to update team',
        variant: 'destructive',
      });
      throw error;
    }
  },

  deleteTeam: async (teamId) => {
    try {
      await deleteTeamAPI(teamId);
      set((state) => ({
        teams: state.teams.filter((team) => team.id !== teamId),
      }));
      toast({
        title: "Success",
        description: "Team deleted successfully!",
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error deleting team:", error);
      toast({
        title: "Error",
        description: message || "Failed to delete team",
        variant: "destructive",
      });
      throw error;
    }
  },
}));

export default useTeamStore; 